import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, Upload, Video, Loader2, Play, Download, Trash2, Users, ArrowRight, X, Circle, FolderOpen } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getAuthHeaders } from '@/hooks/useAuth';
import { createInterview } from '@/services/interviewApi';

// My Library — the creator's standalone content home: upload videos or record
// guest interviews without needing a course first. Uploads live in
// library_items; interview takes are derived across all the user's interviews
// (including course-less ones, which have no other browse surface).

interface LibraryItem { _id: string; name: string; s3Key: string; fileSize?: number; createdAt: string; project?: string | null; }
interface EnhancerItem { _id: string; name: string; s3Key: string | null; url: string | null; thumbnailUrl: string | null; projectName: string | null; createdAt: string; project?: string | null; }
interface LibraryTake { takeId: string; interviewId: string; interviewTitle: string; guestName: string | null; s3Key: string; thumbnailUrl: string | null; durationSec: number; startedAt: string | null; project?: string | null; }
interface LibraryInterview { id: string; title: string; guestName: string | null; productId: string | null; status: string; takeCount: number; updatedAt: string; }

const jsonHeaders = () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' });
const src = (key: string) => (key.startsWith('http') || key.startsWith('/api/')) ? key : `/api/s3/${key}`;

const MyLibraryPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [enhancerItems, setEnhancerItems] = useState<EnhancerItem[]>([]);
  const [takes, setTakes] = useState<LibraryTake[]>([]);
  const [interviews, setInterviews] = useState<LibraryInterview[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [starting, setStarting] = useState(false);
  const [player, setPlayer] = useState<{ url: string; title: string } | null>(null);
  const [projectNames, setProjectNames] = useState<string[]>([]);
  const [assignMenu, setAssignMenu] = useState<{ refId: string; current: string | null } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => {
    fetch('/api/my-library', { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(r.status === 403 ? 'Creator tier required' : 'Failed to load'); return r.json(); })
      .then(d => { setItems(d.items || []); setTakes(d.takes || []); setInterviews(d.interviews || []); setEnhancerItems(d.enhancerItems || []); setProjectNames(d.projectNames || []); })
      .catch(e => toast({ title: e.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true); setUploadPct(0);
    try {
      const { uploadUrl, key } = await fetch('/api/my-library/upload-url', {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ fileName: file.name, fileType: file.type }),
      }).then(r => { if (!r.ok) throw new Error('Could not start the upload'); return r.json(); });
      // XHR for progress — fetch can't report upload progress.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = ev => { if (ev.lengthComputable) setUploadPct(Math.round((ev.loaded / ev.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(file);
      });
      await fetch('/api/my-library', {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ name: file.name.replace(/\.[^.]+$/, ''), s3Key: key, fileSize: file.size }),
      }).then(r => { if (!r.ok) throw new Error('Could not save to your library'); });
      toast({ title: 'Added to your library' });
      load();
    } catch (err: any) {
      toast({ title: err.message || 'Upload failed', variant: 'destructive' });
    } finally { setUploading(false); }
  };

  const startInterview = async () => {
    setStarting(true);
    try {
      const { interview } = await createInterview({ title: 'Guest interview' });
      navigate(`/my-store/interviews/${interview._id}?from=library`);
    } catch (err: any) {
      toast({ title: err.message || "Couldn't start an interview", variant: 'destructive' });
      setStarting(false);
    }
  };

  // Self-recording — camera + mic straight into the library, no guest needed.
  const [recOpen, setRecOpen] = useState(false);
  const [recState, setRecState] = useState<'idle' | 'recording' | 'preview' | 'saving'>('idle');
  const [recElapsed, setRecElapsed] = useState(0);
  const [recPct, setRecPct] = useState(0);
  const recVideoRef = useRef<HTMLVideoElement>(null);
  const recStreamRef = useRef<MediaStream | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recBlobRef = useRef<Blob | null>(null);
  const recTimerRef = useRef<ReturnType<typeof setInterval>>();

  const closeRecorder = () => {
    try { recRef.current?.state !== 'inactive' && recRef.current?.stop(); } catch { /* noop */ }
    clearInterval(recTimerRef.current);
    recStreamRef.current?.getTracks().forEach(t => t.stop());
    recStreamRef.current = null; recRef.current = null; recChunksRef.current = []; recBlobRef.current = null;
    setRecOpen(false); setRecState('idle'); setRecElapsed(0);
  };

  const openRecorder = async () => {
    setRecOpen(true); setRecState('idle'); setRecElapsed(0); recBlobRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: true });
      recStreamRef.current = stream;
      if (recVideoRef.current) { recVideoRef.current.srcObject = stream; recVideoRef.current.muted = true; }
    } catch {
      toast({ title: 'Camera unavailable', description: 'Check camera and microphone permissions.', variant: 'destructive' });
      setRecOpen(false);
    }
  };

  const recStart = () => {
    const stream = recStreamRef.current;
    if (!stream) return;
    recChunksRef.current = [];
    const mime = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'].find(m => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } });
    const rec = new MediaRecorder(stream, { mimeType: mime || undefined, videoBitsPerSecond: 5_000_000 });
    recRef.current = rec;
    rec.ondataavailable = e => { if (e.data?.size) recChunksRef.current.push(e.data); };
    rec.onstop = () => {
      recBlobRef.current = new Blob(recChunksRef.current, { type: rec.mimeType || 'video/webm' });
      if (recVideoRef.current) { recVideoRef.current.srcObject = null; recVideoRef.current.src = URL.createObjectURL(recBlobRef.current); recVideoRef.current.muted = false; }
      setRecState('preview');
    };
    rec.start(1000);
    setRecState('recording'); setRecElapsed(0);
    clearInterval(recTimerRef.current);
    recTimerRef.current = setInterval(() => setRecElapsed(t => t + 1), 1000);
  };

  const recStop = () => { clearInterval(recTimerRef.current); try { recRef.current?.stop(); } catch { /* noop */ } };

  const recRetake = () => {
    recBlobRef.current = null;
    if (recVideoRef.current && recStreamRef.current) { recVideoRef.current.src = ''; recVideoRef.current.srcObject = recStreamRef.current; recVideoRef.current.muted = true; }
    setRecState('idle'); setRecElapsed(0);
  };

  const recSave = async () => {
    const blob = recBlobRef.current;
    if (!blob) return;
    setRecState('saving'); setRecPct(0);
    try {
      const ext = (blob.type || '').includes('mp4') ? 'mp4' : 'webm';
      const name = `Recording ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
      const { uploadUrl, key } = await fetch('/api/my-library/upload-url', {
        method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ fileName: `recording.${ext}`, fileType: blob.type || 'video/webm' }),
      }).then(r => { if (!r.ok) throw new Error('Could not start the upload'); return r.json(); });
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', blob.type || 'video/webm');
        xhr.upload.onprogress = ev => { if (ev.lengthComputable) setRecPct(Math.round((ev.loaded / ev.total) * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(blob);
      });
      await fetch('/api/my-library', {
        method: 'POST', headers: jsonHeaders(),
        body: JSON.stringify({ name, s3Key: key, fileSize: blob.size, durationSec: recElapsed }),
      }).then(r => { if (!r.ok) throw new Error('Could not save to your library'); });
      toast({ title: 'Recording saved to your library' });
      closeRecorder();
      load();
    } catch (err: any) {
      toast({ title: err.message || 'Save failed', variant: 'destructive' });
      setRecState('preview');
    }
  };

  const deleteItem = async (item: LibraryItem) => {
    if (!confirm(`Remove "${item.name}" from your library?`)) return;
    await fetch(`/api/my-library/${item._id}`, { method: 'DELETE', headers: getAuthHeaders() }).catch(() => {});
    setItems(prev => prev.filter(i => i._id !== item._id));
  };

  // Sections: your created projects first, then unfiled content grouped by
  // its source (enhancer project, interview, uploads) so nothing renders as
  // a flat unorganized grid. Assigning an item to a project moves it up.
  type Entry = { kind: 'item'; v: LibraryItem } | { kind: 'enh'; v: EnhancerItem } | { kind: 'take'; v: LibraryTake };
  const allEntries: Entry[] = [
    ...items.map(v => ({ kind: 'item' as const, v })),
    ...enhancerItems.map(v => ({ kind: 'enh' as const, v })),
    ...takes.map(v => ({ kind: 'take' as const, v })),
  ];
  const sourceGroup = (e: Entry) =>
    e.kind === 'item' ? 'Uploads & recordings'
    : e.kind === 'enh' ? (e.v.projectName || 'Enhancer')
    : `${e.v.interviewTitle}${e.v.guestName ? ` — ${e.v.guestName}` : ''}`;
  const groupOf = (e: Entry) => (e.v as any).project || sourceGroup(e);
  const entryDate = (e: Entry) => new Date((e.kind === 'take' ? e.v.startedAt : e.v.createdAt) || 0).getTime();
  const sections = new Map<string, Entry[]>();
  for (const e of allEntries) {
    const g = groupOf(e);
    if (!sections.has(g)) sections.set(g, []);
    sections.get(g)!.push(e);
  }
  const sectionOrder = [...sections.keys()].sort((a2, b2) => {
    const aUser = projectNames.includes(a2) ? 0 : 1;
    const bUser = projectNames.includes(b2) ? 0 : 1;
    if (aUser !== bUser) return aUser - bUser;
    return Math.max(...sections.get(b2)!.map(entryDate)) - Math.max(...sections.get(a2)!.map(entryDate));
  });

  const assign = async (refId: string, project: string) => {
    setAssignMenu(null);
    await fetch('/api/my-library/assign', { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ refId, project }) }).catch(() => {});
    load();
  };
  const newProject = (refId: string) => {
    const name = window.prompt('Project name');
    if (name && name.trim()) assign(refId, name.trim().slice(0, 60));
    else setAssignMenu(null);
  };

  const card: React.CSSProperties = { padding: 18, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' };
  const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  const tile = (key: React.Key, title: string, sub: string, mediaKey: string, thumb: string | null, actions: React.ReactNode, refId?: string, project?: string | null) => (
    <div key={key} style={{ borderRadius: 10, border: '1px solid var(--hj-hairline)', overflow: 'hidden', background: 'var(--hj-surface-1)' }}>
      <button type="button" onClick={() => setPlayer({ url: src(mediaKey), title })} className="relative block w-full" style={{ aspectRatio: '16/9', background: '#000', padding: 0, cursor: 'pointer', border: 0 }}>
        {thumb
          ? <img src={src(thumb)} alt={title} className="w-full h-full" style={{ objectFit: 'cover' }} />
          : <video src={src(mediaKey)} preload="metadata" className="w-full h-full" style={{ objectFit: 'cover', pointerEvents: 'none' }} muted />}
        <span className="absolute inset-0 flex items-center justify-center" style={{ background: 'oklch(0 0 0 / 0.18)' }}><Play className="w-7 h-7" style={{ color: '#fff', fill: '#fff', opacity: 0.9 }} /></span>
      </button>
      <div className="flex items-center gap-2" style={{ padding: '9px 12px' }}>
        <div className="min-w-0 flex-1">
          <div className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>{title}</div>
          <div className="truncate" style={{ fontSize: 11, color: 'var(--hj-ink-3)' }}>{project ? `${project} · ` : ''}{sub}</div>
        </div>
        {refId && (
          <button type="button" onClick={() => setAssignMenu({ refId, current: project || null })} title="Add to project" className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-3)', cursor: 'pointer' }}><FolderOpen className="w-3.5 h-3.5" /></button>
        )}
        {actions}
      </div>
    </div>
  );

  return (
    <main style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      <div className="mx-auto" style={{ maxWidth: 1120, padding: '0 24px 96px' }}>
        <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
          <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
            <ChevronLeft size={12} strokeWidth={2} /> Home
          </Link>
          <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
          <span style={{ color: 'var(--hj-ink)' }}>My Library</span>
        </div>

        <header style={{ margin: '4px 0 24px', padding: '22px 24px 20px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
          <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-beige-ink-2)' }}>My Library</div>
          <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(30px, 4vw, 44px)', lineHeight: 1.05, letterSpacing: '-0.025em', color: 'var(--hj-beige-ink)' }}>My Library.</h1>
          <p className="m-0" style={{ marginTop: 10, color: 'var(--hj-beige-ink)', fontSize: 14.5, maxWidth: 560, lineHeight: 1.5 }}>
            Your videos, in one place — upload files or record an interview with a remote guest. Use them anywhere: courses, lessons, or on their own.
          </p>
        </header>

        {/* The two ways in */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" style={{ marginBottom: 28 }}>
          <button type="button" onClick={openRecorder} className="text-left transition-colors" style={{ ...card, cursor: 'pointer' }}>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 12, background: 'oklch(0.6 0.2 25)', color: '#fff' }}>
                <Circle className="w-5 h-5" style={{ fill: '#fff' }} />
              </span>
              <div>
                <div style={{ fontSize: 15.5, fontWeight: 700 }}>Record a video</div>
                <div style={{ marginTop: 2, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>Camera on, hit record — it saves right here.</div>
              </div>
            </div>
          </button>
          <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()} className="text-left transition-colors" style={{ ...card, cursor: uploading ? 'default' : 'pointer' }}>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--hj-blue)', color: 'oklch(0.18 0.005 70)' }}>
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              </span>
              <div>
                <div style={{ fontSize: 15.5, fontWeight: 700 }}>{uploading ? `Uploading — ${uploadPct}%` : 'Upload a video'}</div>
                <div style={{ marginTop: 2, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>Drop in footage from your camera roll or desktop.</div>
              </div>
            </div>
          </button>
          <button type="button" disabled={starting} onClick={startInterview} className="text-left transition-colors" style={{ ...card, cursor: starting ? 'default' : 'pointer' }}>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--hj-yellow)', color: 'oklch(0.18 0.005 70)' }}>
                {starting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Video className="w-5 h-5" />}
              </span>
              <div>
                <div style={{ fontSize: 15.5, fontWeight: 700 }}>Interview a guest</div>
                <div style={{ marginTop: 2, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>Send a link, record them remotely — takes land right here.</div>
              </div>
            </div>
          </button>
          <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={handleUpload} />
        </div>

        {loading ? (
          <div className="flex justify-center" style={{ padding: '60px 0' }}><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} /></div>
        ) : (
          <>
            {/* Interviews are ephemeral meetings — the takes below are the
                artifact. Only surface a session someone might still be IN
                (active + touched in the last 6h) so a live guest isn't lost. */}
            {(() => {
              const live = interviews.filter(iv => iv.status === 'active' && Date.now() - new Date(iv.updatedAt).getTime() < 6 * 3600_000);
              if (!live.length) return null;
              return (
                <section style={{ marginBottom: 28 }}>
                  <div className="mono-label" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}>In progress</div>
                  <div className="flex flex-col gap-2">
                    {live.slice(0, 4).map(iv => (
                      <Link key={iv.id} to={`/my-store/interviews/${iv.id}?from=library`} className="flex items-center gap-3 no-underline transition-colors" style={{ ...card, padding: '12px 16px', color: 'var(--hj-ink)' }}>
                        <Users className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-ink-3)' }} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate" style={{ fontSize: 13.5, fontWeight: 600 }}>{iv.title}{iv.guestName ? ` — ${iv.guestName}` : ''}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--hj-ink-3)' }}>{iv.takeCount} take{iv.takeCount !== 1 ? 's' : ''} · {fmtDate(iv.updatedAt)}</div>
                        </div>
                        <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-ink-3)' }} />
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })()}

            {/* All videos — grouped sections */}
            {allEntries.length > 0 ? (
              <section>
                <div className="mono-label" style={{ marginBottom: 14, color: 'var(--hj-ink-3)' }}>Videos ({allEntries.length})</div>
                {sectionOrder.map(g => (
                  <div key={g} style={{ marginBottom: 24 }}>
                    <div className="flex items-baseline gap-2" style={{ marginBottom: 10 }}>
                      <h2 className="font-hj-display m-0" style={{ fontSize: 17, fontWeight: 700, color: 'var(--hj-ink)', letterSpacing: '-0.01em' }}>{g}</h2>
                      <span className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>{sections.get(g)!.length}</span>
                      {projectNames.includes(g) && <span className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>· project</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sections.get(g)!.sort((x, y) => entryDate(y) - entryDate(x)).map(e => {
                        if (e.kind === 'item') { const item = e.v; return tile(
                          `u-${item._id}`, item.name, `Upload · ${fmtDate(item.createdAt)}`, item.s3Key, null,
                          <>
                            <a href={src(item.s3Key)} download title="Download" className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)' }}><Download className="w-3.5 h-3.5" /></a>
                            <button type="button" onClick={() => deleteItem(item)} title="Remove" className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-3)', cursor: 'pointer' }}><Trash2 className="w-3.5 h-3.5" /></button>
                          </>,
                          `u-${item._id}`, item.project
                        ); }
                        if (e.kind === 'enh') { const en = e.v; return tile(
                          en._id, en.name, `Enhancer · ${fmtDate(en.createdAt)}`,
                          (en.s3Key || en.url || ''), en.thumbnailUrl,
                          <a href={(en.s3Key || en.url || '').startsWith('http') || (en.s3Key || en.url || '').startsWith('/api/') ? (en.s3Key || en.url!) : `/api/s3/${en.s3Key || en.url}`} download title="Download" className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)' }}><Download className="w-3.5 h-3.5" /></a>,
                          en._id, en.project
                        ); }
                        const t = e.v; return tile(
                          `t-${t.interviewId}-${t.takeId}`, t.guestName || t.interviewTitle, `Interview · ${fmtDate(t.startedAt)}`, t.s3Key, t.thumbnailUrl,
                          <>
                            <a href={src(t.s3Key)} download title="Download" className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)' }}><Download className="w-3.5 h-3.5" /></a>
                            <Link to={`/my-store/interviews/${t.interviewId}?from=library`} title="Open interview" className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)' }}><Users className="w-3.5 h-3.5" /></Link>
                          </>,
                          `t-${t.interviewId}-${t.takeId}`, t.project
                        );
                      })}
                    </div>
                  </div>
                ))}
              </section>
            ) : interviews.length === 0 && (
              <div className="text-center" style={{ padding: '48px 24px', borderRadius: 'var(--hj-r-md)', border: '1.5px dashed var(--hj-hairline)', color: 'var(--hj-ink-3)' }}>
                <Video className="w-9 h-9 mx-auto" style={{ marginBottom: 10 }} />
                <p className="m-0" style={{ fontSize: 14 }}>Nothing here yet — upload a video or interview a guest to get started.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add-to-project menu */}
      {assignMenu && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 70, background: 'oklch(0 0 0 / 0.6)', padding: 20 }} onClick={() => setAssignMenu(null)}>
          <div style={{ width: '100%', maxWidth: 360, maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-hairline-soft)', fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)' }}>Add to project</div>
            <div style={{ padding: 8 }}>
              {projectNames.map(pn => (
                <button key={pn} type="button" onClick={() => assign(assignMenu.refId, pn)} className="w-full text-left" style={{ padding: '9px 12px', borderRadius: 8, border: 0, background: assignMenu.current === pn ? 'var(--hj-surface-2)' : 'transparent', color: 'var(--hj-ink)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {pn}{assignMenu.current === pn ? ' ✓' : ''}
                </button>
              ))}
              <button type="button" onClick={() => newProject(assignMenu.refId)} className="w-full text-left" style={{ padding: '9px 12px', borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hj-ink-2)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>+ New project…</button>
              {assignMenu.current && (
                <button type="button" onClick={() => assign(assignMenu.refId, '')} className="w-full text-left" style={{ padding: '9px 12px', borderRadius: 8, border: 0, background: 'transparent', color: 'var(--hj-ink-3)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' }}>Remove from project</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Self-recorder modal */}
      {recOpen && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 60, background: 'oklch(0 0 0 / 0.72)', padding: 20 }}>
          {/* Capped to the visible viewport so Record/Stop/Save stay on screen on phones. */}
          <div className="flex flex-col" style={{ width: '100%', maxWidth: 780, maxHeight: 'calc(100dvh - 40px)', overflowY: 'auto', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)' }}>
            <div className="flex items-center gap-2" style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--hj-ink)' }}>
                {recState === 'recording' ? `Recording — ${Math.floor(recElapsed / 60)}:${String(recElapsed % 60).padStart(2, '0')}` : recState === 'preview' ? 'Review your recording' : recState === 'saving' ? `Saving — ${recPct}%` : 'Record a video'}
              </span>
              {recState === 'recording' && <span style={{ width: 10, height: 10, borderRadius: 999, background: 'oklch(0.6 0.2 25)' }} className="animate-pulse" />}
              <button type="button" onClick={closeRecorder} disabled={recState === 'saving'} className="inline-flex items-center justify-center flex-shrink-0" style={{ marginLeft: 'auto', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
            </div>
            <video ref={recVideoRef} autoPlay playsInline controls={recState === 'preview'} style={{ width: '100%', maxHeight: '58dvh', display: 'block', background: '#000', transform: recState === 'preview' ? 'none' : 'scaleX(-1)' }} />
            <div className="flex items-center justify-center gap-3" style={{ padding: 14 }}>
              {recState === 'idle' && (
                <button type="button" onClick={recStart} className="inline-flex items-center gap-2" style={{ padding: '11px 24px', borderRadius: 999, background: 'oklch(0.6 0.2 25)', border: 0, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  <Circle className="w-4 h-4" style={{ fill: '#fff' }} /> Record
                </button>
              )}
              {recState === 'recording' && (
                <button type="button" onClick={recStop} className="inline-flex items-center gap-2" style={{ padding: '11px 24px', borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  <span style={{ width: 12, height: 12, borderRadius: 2, background: 'oklch(0.6 0.2 25)' }} /> Stop
                </button>
              )}
              {recState === 'preview' && (
                <>
                  <button type="button" onClick={recSave} className="inline-flex items-center gap-2" style={{ padding: '11px 24px', borderRadius: 999, background: 'var(--hj-blue)', border: 0, color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Save to library</button>
                  <button type="button" onClick={recRetake} style={{ padding: '11px 20px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Re-record</button>
                </>
              )}
              {recState === 'saving' && <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />}
            </div>
          </div>
        </div>
      )}

      {/* Player modal */}
      {player && (
        <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 60, background: 'oklch(0 0 0 / 0.72)', padding: 20 }} onClick={() => setPlayer(null)}>
          <div className="flex flex-col" style={{ width: '100%', maxWidth: 860, background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2" style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-hairline-soft)' }}>
              <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{player.title}</span>
              <button type="button" onClick={() => setPlayer(null)} className="inline-flex items-center justify-center flex-shrink-0" style={{ marginLeft: 'auto', width: 32, height: 32, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'transparent', color: 'var(--hj-ink-2)', cursor: 'pointer' }}><X className="w-4 h-4" /></button>
            </div>
            <video src={player.url} controls autoPlay style={{ width: '100%', maxHeight: '70vh', display: 'block', background: '#000' }} />
          </div>
        </div>
      )}
    </main>
  );
};

export default MyLibraryPage;
